import React, { useState } from "react";

export default function FinancialCalc() {
  // EMI
  const [loan, setLoan] = useState("");
  const [rate, setRate] = useState("");
  const [tenure, setTenure] = useState("");

  // Compound
  const [principal, setPrincipal] = useState("");
  const [cRate, setCRate] = useState("");
  const [years, setYears] = useState("");

  // SIP
  const [sip, setSip] = useState("");
  const [sipRate, setSipRate] = useState("");
  const [sipYears, setSipYears] = useState("");

  // Results
  const [emiResult, setEmiResult] = useState(null);
  const [ciResult, setCiResult] = useState(null);
  const [sipResult, setSipResult] = useState(null);

  // EMI Calculation
  const calculateEMI = () => {
    if (!loan || !rate || !tenure) return;
    const P = parseFloat(loan);
    const r = parseFloat(rate) / 12 / 100;
    const n = parseFloat(tenure) * 12;

    const emi =
      (P * r * Math.pow(1 + r, n)) /
      (Math.pow(1 + r, n) - 1);

    setEmiResult({
      emi: emi.toFixed(0),
      total: (emi * n).toFixed(0),
      interest: (emi * n - P).toFixed(0),
    });
  };

  // Compound Interest
  const calculateCI = () => {
    if (!principal || !cRate || !years) return;
    const P = parseFloat(principal);
    const r = parseFloat(cRate) / 100;
    const t = parseFloat(years);

    const A = P * Math.pow(1 + r, t);

    setCiResult({
      maturity: A.toFixed(0),
      interest: (A - P).toFixed(0),
    });
  };

  // SIP Calculation
  const calculateSIP = () => {
    if (!sip || !sipRate || !sipYears) return;
    const P = parseFloat(sip);
    const r = parseFloat(sipRate) / 12 / 100;
    const n = parseFloat(sipYears) * 12;

    const FV =
      P *
      ((Math.pow(1 + r, n) - 1) / r) *
      (1 + r);

    setSipResult({
      invested: (P * n).toFixed(0),
      value: FV.toFixed(0),
      gain: (FV - P * n).toFixed(0),
    });
  };

  return (
    <div className="min-h-screen bg-black text-white [&_h2]:text-cyan-200 p-4 space-y-6">
      <h1 className="text-xl font-bold text-center">💰 Financial Calculator</h1>

      {/* EMI */}
      <div className="bg-gray-900 p-4 rounded-2xl space-y-2">
        <h2 className="text-lg">🏠 EMI Calculator</h2>
        <input
          type="number"
          placeholder="Loan Amount ₹"
          value={loan}
          onChange={(e) => setLoan(e.target.value)}
          className="w-full p-2 bg-black border border-gray-700 rounded"
        />
        <input
          type="number"
          placeholder="Interest %"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          className="w-full p-2 bg-black border border-gray-700 rounded"
        />
        <input
          type="number"
          placeholder="Tenure (Years)"
          value={tenure}
          onChange={(e) => setTenure(e.target.value)}
          className="w-full p-2 bg-black border border-gray-700 rounded"
        />
        <button
          onClick={calculateEMI}
          className="w-full bg-green-600 p-2 rounded mt-2"
        >
          Calculate EMI
        </button>

        {emiResult && (
          <div className="text-sm mt-2 space-y-1">
            <p>EMI: ₹ {emiResult.emi}</p>
            <p>Total Payment: ₹ {emiResult.total}</p>
            <p>Interest: ₹ {emiResult.interest}</p>
          </div>
        )}
      </div>

      {/* Compound Interest */}
      <div className="bg-gray-900 p-4 rounded-2xl space-y-2">
        <h2 className="text-lg">📈 Compound Interest</h2>
        <input
          type="number"
          placeholder="Principal ₹"
          value={principal}
          onChange={(e) => setPrincipal(e.target.value)}
          className="w-full p-2 bg-black border border-gray-700 rounded"
        />
        <input
          type="number"
          placeholder="Rate %"
          value={cRate}
          onChange={(e) => setCRate(e.target.value)}
          className="w-full p-2 bg-black border border-gray-700 rounded"
        />
        <input
          type="number"
          placeholder="Years"
          value={years}
          onChange={(e) => setYears(e.target.value)}
          className="w-full p-2 bg-black border border-gray-700 rounded"
        />
        <button
          onClick={calculateCI}
          className="w-full bg-green-600 p-2 rounded mt-2"
        >
          Calculate
        </button>

        {ciResult && (
          <div className="text-sm mt-2 space-y-1">
            <p>Maturity: ₹ {ciResult.maturity}</p>
            <p>Interest: ₹ {ciResult.interest}</p>
          </div>
        )}
      </div>

      {/* SIP */}
      <div className="bg-gray-900 p-4 rounded-2xl space-y-2">
        <h2 className="text-lg">🏦 SIP Calculator</h2>
        <input
          type="number"
          placeholder="Monthly Investment ₹"
          value={sip}
          onChange={(e) => setSip(e.target.value)}
          className="w-full p-2 bg-black border border-gray-700 rounded"
        />
        <input
          type="number"
          placeholder="Return %"
          value={sipRate}
          onChange={(e) => setSipRate(e.target.value)}
          className="w-full p-2 bg-black border border-gray-700 rounded"
        />
        <input
          type="number"
          placeholder="Years"
          value={sipYears}
          onChange={(e) => setSipYears(e.target.value)}
          className="w-full p-2 bg-black border border-gray-700 rounded"
        />
        <button
          onClick={calculateSIP}
          className="w-full bg-green-600 p-2 rounded mt-2"
        >
          Calculate SIP
        </button>

        {sipResult && (
          <div className="text-sm mt-2 space-y-1">
            <p>Invested: ₹ {sipResult.invested}</p>
            <p>Future Value: ₹ {sipResult.value}</p>
            <p className="text-green-400">
              Gain: ₹ {sipResult.gain}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}